import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import sleep from "@/utils/sleep";
import {
    initialState as commonInitialState,
    ISimulatorState as ICommonState,
    mutations as commonMutations,
    actions as commonActions,
    IVtkFile,
    SaveStatus,
    Status
} from "@/modules/simulator/store";
import api from "../api";

export interface ISimulatorState extends ICommonState {
    quota_flux: number;
    stageData: {
        quota_flux?: number;
    };
}

const initialState: ISimulatorState = {
    ...commonInitialState,
    quota_flux: 0.0,
    stageData: {}
};

const mutations: MutationTree<ISimulatorState> = {
    ...commonMutations,
    UPDATE_DATA(
        state,
        data: {
            quota_flux?: number;
        }
    ): void {
        for (const [key, value] of Object.entries(data)) {
            state[key] = value;
        }
        state.is_valid_results = false;
    },
    UPDATE_STAGE_DATA(
        state,
        data: {
            quota_flux?: number;
        }
    ): void {
        for (const [key, value] of Object.entries(data)) {
            state.stageData[key] = value;
        }
    },
    CLEAR_STAGE_DATA(state) {
        state.stageData = {};
    },
    INITIALIZE(
        state,
        data: {
            quota_flux: number;
            status: Status;
            log: string;
            results: Object | null;
            started_at: string | null;
            time_elapsed: number;
            is_valid_results: boolean;
            vtk_files: IVtkFile[];
        }
    ) {
        state.quota_flux = data.quota_flux;
        state.status = data.status;
        state.log = data.log;
        state.results = data.results;
        state.started_at = data.started_at;
        state.time_elapsed = data.time_elapsed;
        state.is_valid_results = data.is_valid_results;
        state.vtk_files = data.vtk_files;
    }
};

const actions: ActionTree<ISimulatorState, any> = {
    ...commonActions,
    async initialize({ state, dispatch }): Promise<void> {
        await dispatch("getData");
        if (state.status === Status.running) {
            dispatch("startStatusCheck");
        }
    },
    async getData({ commit }): Promise<void> {
        const { data } = await api.simulator.getData();
        commit(
            "INITIALIZE",
            data as {
                quota_flux: number;
                status: Status;
                log: string;
                results: Object | null;
                started_at: string | null;
                time_elapsed: number;
                is_valid_results: boolean;
                vtk_files: IVtkFile[];
            }
        );
    },
    async updateData(
        { commit, dispatch },
        data: {
            quota_flux?: number;
        }
    ): Promise<void> {
        commit("UPDATE_DATA", data);
        commit("UPDATE_STAGE_DATA", data);
        commit("UPDATE_SAVE_STATUS", SaveStatus.changed);
        await dispatch("updateSaveTimer");
    },
    updateSaveTimer({ state, commit }): void {
        commit("UPDATE_SAVE_TIMER", async () => {
            const data = {};

            for (const [key, value] of Object.entries(state.stageData)) {
                data[key] = value;
            }

            commit("UPDATE_SAVE_STATUS", SaveStatus.saving);
            await api.simulator.postData(data);

            commit("CLEAR_STAGE_DATA");

            await sleep(1000);
            commit("UPDATE_SAVE_STATUS", SaveStatus.saved);
        });
    },
    async simulate({ dispatch }): Promise<void> {
        const promiseResponse = api.simulator.simulate();
        await dispatch("startSimulation", promiseResponse);
        promiseResponse.then(() => {
            dispatch("startStatusCheck");
        });
    },
    async stopSimulation(): Promise<void> {
        await api.simulator.stopSimulation();
    },
    startStatusCheck({ commit }): void {
        async function check() {
            const {
                data: {
                    status,
                    log,
                    results,
                    started_at,
                    time_elapsed,
                    vtk_files
                }
            } = await api.simulator.getStatus();

            if (status === Status.running) {
                setTimeout(check, 5000);
            } else {
                commit("UPDATE_STATUS", status);
                commit("UPDATE_LOG", log);
                commit("UPDATE_RESULTS", results);
                commit("UPDATE_STARTED_AT", started_at);
                commit("UPDATE_TIME_ELAPSED", time_elapsed);
                commit("UPDATE_VTK_FILES", vtk_files);
            }
        }

        check();
    },
    async updateFromReservorInfo(
        { commit, state },
        reservoirInfo: { quota_flux: number }
    ): Promise<void> {
        const data: {
            quota_flux: number;
        } = {
            quota_flux: reservoirInfo.quota_flux
        };

        commit("UPDATE_DATA", data);

        await api.simulator.postData(data);
    }
};

const getters: GetterTree<ISimulatorState, any> = {};

export const simulator: Module<ISimulatorState, any> = {
    namespaced: true,
    state: initialState,
    mutations,
    actions,
    getters
};
